import type {
  TokenRow,
  TokenVocabularyRow,
} from '@graphql/queries/PerceptionModelQueries';

/**
 * Token search for the perception data explorer.
 *
 * The vocabulary and the matching both happen in the database now. This module
 * only shapes what comes back: the browser used to download every product's
 * token maps to do the same work, which was 21 MB on a 16k-product universe.
 *
 * Tokens are stored per product as { token: prevalence }, already filtered to
 * those above the export threshold, so presence means "a meaningful share of
 * reviews mentioned it" rather than "mentioned once".
 */

export type TokenKind = 'flavor' | 'texture' | 'sensation';

export const TOKEN_KINDS: TokenKind[] = ['flavor', 'texture', 'sensation'];

/** One selectable token. The same word can exist in more than one kind. */
export type TokenOption = {
  kind: TokenKind;
  token: string;
  /** how many modelled products carry it, shown so dead ends are obvious */
  productCount: number;
};

/** A stable key, since the same word can be both a flavor and a sensation. */
export const tokenKey = (t: Pick<TokenOption, 'kind' | 'token'>): string =>
  `${t.kind}:${t.token}`;

const isKind = (k: string): k is TokenKind =>
  (TOKEN_KINDS as string[]).includes(k);

/**
 * Shape the vocabulary view's rows into options.
 *
 * Sorted by kind, then by descending product count: the tokens that can
 * actually narrow a search are worth showing first, and a token on two products
 * is visibly a dead end before it is picked.
 */
export const buildTokenVocabulary = (
  rows: TokenVocabularyRow[],
): TokenOption[] =>
  (rows ?? [])
    .filter((r) => r && isKind(r.kind) && !!r.token)
    .map((r) => ({
      kind: r.kind as TokenKind,
      token: r.token,
      productCount: Number(r.productCount) || 0,
    }))
    .sort(
      (a, b) =>
        TOKEN_KINDS.indexOf(a.kind) - TOKEN_KINDS.indexOf(b.kind) ||
        b.productCount - a.productCount ||
        a.token.localeCompare(b.token),
    );

/**
 * Products carrying **every** selected token.
 *
 * The query filters by token, which is an OR, so the AND is completed here by
 * counting how many of the selected tokens each product matched. Conjunctive
 * because that is what a search box implies: adding a token should narrow the
 * result, not widen it.
 *
 * Matching is on (kind, token), not token alone — "sharp" exists as both a
 * flavor and a sensation, and treating them as one would silently merge two
 * different questions.
 */
export const matchedProducts = (
  rows: TokenRow[],
  selected: TokenOption[],
): Set<string> => {
  if (selected.length === 0) return new Set();

  const wanted = new Set(selected.map(tokenKey));
  const hits = new Map<string, Set<string>>();

  (rows ?? []).forEach((row) => {
    if (!row || typeof row.productName !== 'string') return;
    const key = `${row.kind}:${row.token}`;
    if (!wanted.has(key)) return; // same word, other kind
    const seen = hits.get(row.productName) ?? new Set<string>();
    seen.add(key);
    hits.set(row.productName, seen);
  });

  const out = new Set<string>();
  hits.forEach((seen, product) => {
    if (seen.size === wanted.size) out.add(product);
  });
  return out;
};
